import {
  Accordion,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  Tooltip,
} from '@mantine/core';
import { IconFlag, IconNotes } from '@tabler/icons-react';
import { useState } from 'react';
import type { ClinicalNote } from '../../clients/data/types';
import type { SessionAutosaveStatus } from '../hooks/useSessionAutosave';
import { SessionAutosaveStatusText } from '../components/SessionAutosaveStatusText';
import { PsychotherapyNoteSection } from '@/components/psychotherapy/PsychotherapyNoteSection';
import { usePermissions } from '@/contexts/PermissionContext';
import { canViewPsychotherapyNote } from '@/lib/chart/chartDocumentAccess';

interface ClinicalNotesTabProps {
  notes: ClinicalNote[];
  sessionNotes?: string;
  onSessionNotesChange?: (value: string) => void;
  sessionNotesStatus?: SessionAutosaveStatus;
  sessionNotesError?: string | null;
  onSessionNotesBlur?: () => void;
  onSessionNotesRetry?: () => void;
  /** Group Therapy: hide one member's historical notes under the shared session. */
  hidePastClinicalNotes?: boolean;
  appointmentId?: string;
  patientId?: string;
  encounterId?: string;
}

const NOTE_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'Progress Note', label: 'Progress Notes' },
  { value: 'Intake Note', label: 'Intake Notes' },
  { value: 'Risk Assessment', label: 'Risk Assessments' },
  { value: 'Psychotherapy Note', label: 'Psychotherapy Notes' },
];

function noteStatusColor(status: ClinicalNote['noteStatus']): string {
  switch (status) {
    case 'Signed': return 'green';
    case 'Locked': return 'blue';
    case 'Draft': return 'yellow';
    case 'Pending co-signature': return 'orange';
    case 'Private': return 'gray';
    case 'Complete': return 'green';
    default: return 'gray';
  }
}

export function ClinicalNotesTab({
  notes,
  sessionNotes = '',
  onSessionNotesChange,
  sessionNotesStatus = 'idle',
  sessionNotesError = null,
  onSessionNotesBlur,
  onSessionNotesRetry,
  hidePastClinicalNotes = false,
  appointmentId,
  patientId,
  encounterId,
}: ClinicalNotesTabProps): JSX.Element {
  const { role } = usePermissions();
  const mayViewPsychotherapyNote = canViewPsychotherapyNote(role);
  const [filter, setFilter] = useState('all');

  const filtered = filter === 'all' ? notes : notes.filter((n) => n.noteType === filter);
  const sorted = [...filtered].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Stack gap="sm">
      {onSessionNotesChange != null && (
        <>
          <Stack gap={4}>
            <Group gap={6} align="center">
              <IconNotes size={16} color="var(--mantine-color-dimmed)" />
              <Text size="sm" fw={600}>
                Session Notes
              </Text>
            </Group>
            <Text size="xs" c="dimmed" lh={1.5}>
              Scratch notes autosave to the current appointment. They may be used as context for AI draft
              generation. They are not included in the signed progress note unless you deliberately
              incorporate them.
            </Text>
          </Stack>
          <Textarea
            value={sessionNotes}
            onChange={(e) => onSessionNotesChange(e.currentTarget.value)}
            onBlur={() => onSessionNotesBlur?.()}
            placeholder="Observations, themes, interventions, or reminders for the AI draft…"
            size="sm"
            minRows={6}
            autosize
            maxRows={14}
            styles={{ input: { paddingTop: 10, paddingBottom: 10 } }}
          />
          <Stack gap={6}>
            <Tooltip label="Coming soon">
              <Button
                fullWidth
                size="sm"
                variant="light"
                color="gray"
                leftSection={<IconFlag size={16} />}
                disabled
                aria-disabled
                styles={{
                  root: {
                    border: '1px solid var(--mantine-color-gray-3)',
                    color: 'var(--mantine-color-gray-6)',
                  },
                }}
              >
                Flag for AI
              </Button>
            </Tooltip>
            <SessionAutosaveStatusText
              status={sessionNotesStatus}
              error={sessionNotesError}
              onRetry={onSessionNotesRetry}
            />
          </Stack>
          {mayViewPsychotherapyNote && appointmentId && patientId ? (
            <>
              <Divider />
              <PsychotherapyNoteSection
                appointmentId={appointmentId}
                patientRef={{ reference: `Patient/${patientId}` }}
                encounterRef={
                  encounterId ? { reference: `Encounter/${encounterId}` } : undefined
                }
                compact
              />
            </>
          ) : null}
          {!hidePastClinicalNotes ? (
            <>
              <Divider />
              <Text size="sm" fw={600}>
                Past Clinical Notes
              </Text>
              <Text size="xs" c="dimmed">
                Previous signed and draft notes from earlier appointments in this episode. The current
                appointment is excluded.
              </Text>
            </>
          ) : null}
        </>
      )}

      {!hidePastClinicalNotes ? (
        <>
          <Select
            size="xs"
            placeholder="Filter by type"
            data={NOTE_TYPE_FILTER_OPTIONS}
            value={filter}
            onChange={(v) => setFilter(v ?? 'all')}
          />

          {sorted.length === 0 ? (
            <Box py="lg">
              <Text size="sm" c="dimmed" ta="center">
                No past appointment-linked clinical notes yet.
              </Text>
            </Box>
          ) : (
            <Accordion variant="separated" radius="sm">
              {sorted.map((note) => (
                <Accordion.Item key={note.id} value={note.id}>
                  <Accordion.Control>
                    <Stack gap={2}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text size="sm" fw={500} lineClamp={1}>{note.title}</Text>
                        <Badge size="xs" variant="light" color={noteStatusColor(note.noteStatus)}>
                          {note.noteStatus}
                        </Badge>
                      </Group>
                      <Group gap="xs">
                        <Text size="xs" c="dimmed">{note.date}</Text>
                        <Text size="xs" c="dimmed">·</Text>
                        <Text size="xs" c="dimmed">{note.provider}</Text>
                      </Group>
                    </Stack>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {note.soapNote ? (
                      <Stack gap="sm">
                        <Stack gap={2}>
                          <Text size="xs" fw={600} c="gray.7">Subjective</Text>
                          <Text size="xs" c="gray.8">{note.soapNote.subjective}</Text>
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" fw={600} c="gray.7">Objective</Text>
                          <Text size="xs" c="gray.8">{note.soapNote.objective}</Text>
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" fw={600} c="gray.7">Assessment</Text>
                          <Text size="xs" c="gray.8">{note.soapNote.assessment}</Text>
                        </Stack>
                        <Stack gap={2}>
                          <Text size="xs" fw={600} c="gray.7">Plan</Text>
                          <Text size="xs" c="gray.8">{note.soapNote.plan}</Text>
                        </Stack>
                      </Stack>
                    ) : (
                      <Text size="xs" c="dimmed" fs="italic">
                        {note.noteStatus === 'Private' ? 'Private psychotherapy note — content restricted.' : 'No SOAP content available.'}
                      </Text>
                    )}
                    {note.cptCodes && note.cptCodes.length > 0 && (
                      <Group gap={4} mt="xs">
                        {note.cptCodes.map((code) => (
                          <Badge key={code} size="xs" variant="outline" color="gray">{code}</Badge>
                        ))}
                      </Group>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </>
      ) : null}
    </Stack>
  );
}
